/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 구분선 블록을 정의한다.
 *
 * @file /src/rootin/blocks/divider/Divider.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 26.
 */
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Editor } from '@rootin/core/Editor';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';
import { Language, LanguageText } from '@utils/Language';
import './Divider.css';

export class Divider extends Block {
    /**
     * 블록타입
     */
    static type = 'divider';

    /**
     * 블록종류
     */
    static kind: 'inline' | 'atomic' = 'atomic';

    /**
     * 블록아이콘
     */
    static icon: string =
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-square-centerline-dashed-vertical-icon lucide-square-centerline-dashed-vertical"><path d="M21 3v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3"/><path d="M21 21v-3a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v3"/><path d="M4 12H2"/><path d="M10 12H8"/><path d="M16 12h-2"/><path d="M22 12h-2"/></svg>';

    /**
     * 블록명
     */
    static label: LanguageText = Language.get('rootin.blocks.divider.label');

    /**
     * 구분선 DOM
     */
    $divider: Dom = null;

    /**
     * 인용 블록을 정의한다.
     *
     * @param editor - 이 블록이 속한 에디터
     */
    constructor(editor: Editor) {
        super(editor);
    }

    /**
     * 구분선 DOM 을 가져온다.
     *
     * @returns 구분선 DOM
     */
    $getDivider(): Dom {
        if (this.$divider === null) {
            this.$divider = Html.create('div', { 'data-role': 'divider' });
        }

        return this.$divider;
    }

    /**
     * 블록을 렌더링한다.
     */
    async render(): Promise<void> {
        this.$getBlock().append(this.$getDivider());
    }

    /**
     * 블록이 비었는지 확인한다.
     *
     * @returns 블록이 비었는지 여부
     */
    isEmpty(): boolean {
        return false;
    }

    /**
     * 블록값을 가져온다.
     * element 변수가 존재할 경우, 해당 엘리먼트의 기본값을 가져온다.
     *
     * @param element - 기본값을 가져오려는 엘리먼트 (NULL 인 경우 전체 블록데이터)
     * @returns 블록값
     */
    getValue(element: Element = null): any {
        return {
            id: this.getId(),
            type: this.getType(),
        };
    }

    /**
     * 블록값을 지정한다.
     *
     * @param value - 블록값
     * @param is_default_value - 기본값 여부
     */
    async setValue(value: { [key: string]: any }, is_default_value: boolean = false): Promise<void> {}

    /**
     * 블록내에서 Enter 키입력이 발생했을 때 이벤트를 처리한다.
     *
     * @param element - 실제로 Enter 키입력이 발생한 엘리먼트 (NULL 이면 블록자체)
     */
    onEnter(element: Element): void {
        const container = this.getParent();
        if (container.blocks.includes('paragraph') === false) {
            return;
        }

        const ParagraphClass = this.getBlockClass('paragraph');
        if (ParagraphClass === null) {
            return;
        }

        container.splitBlock(ParagraphClass.create(this.editor), this, element, 'text');
    }
}
